import { AutoAddNewBidderSeats } from '@pubstack/common/src/bidder-rule'
import { FunctionComponent } from 'react'
import { Toggle } from '~/components/Toggle'
import { BIDDER_RULE_FORM_SECTION_LABELS, BidderRuleFormSection, storedSectionOf } from './bidderRuleSections'

type Props = {
  /** The sections available to this scope, in reading order. A scope with neither active gets no section at all. */
  sections: BidderRuleFormSection[]
  autoAdd: AutoAddNewBidderSeats
  onToggleAutoAdd: (section: BidderRuleFormSection, enabled: boolean) => void
}

// The `server_side` flag also governs Spark for Pubs seats — `formSectionOfBidderSeat` folds them into
// this section rather than splitting a fourth one out for them — so the caption names both rather than
// letting a Spark seat's auto-add read as unexplained.
const AUTO_ADD_CAPTION_TYPE: Record<BidderRuleFormSection, string> = {
  client_side: 'Client-side',
  server_side: 'Server-side or Spark for Pubs',
}

/**
 * One rule about the section as a whole rather than about any seat in it, so it sits with the other
 * rule-level settings above the seat lists instead of inside them. Each row cards up the same way a
 * seat row does — a bordered pill with the toggle at the end — so the setting above the list reads as
 * one more row of it rather than as a form field bolted on top.
 */
export const BidderRuleAutoAddSection: FunctionComponent<Props> = ({ sections, autoAdd, onToggleAutoAdd }) => {
  if (sections.length === 0) return null

  return (
    <section className="flex flex-col gap-2">
      <h2 className="font-medium">Automatic seat assignment</h2>
      <div className="flex flex-col gap-3">
        {sections.map((section) => {
          const value = autoAdd[storedSectionOf(section)]
          const label = BIDDER_RULE_FORM_SECTION_LABELS[section]
          return (
            // Not a `<label>`: it would pass the toggle's click on to the toggle's hidden checkbox and flip the value twice.
            <div key={section} onClick={() => onToggleAutoAdd(section, !value)} className="flex cursor-pointer flex-col gap-1 rounded-lg border border-slate-200 bg-white px-4 py-3.5">
              <div className="flex items-center gap-3">
                {/* No chip: the sentence beside it already names the integration in bold, so the pill was
                    the same word twice on one line. */}
                <span className="text-base">
                  Automatically enable new <strong>{label.toLowerCase()}</strong> seats
                </span>
                <Toggle
                  className="ml-auto shrink-0"
                  value={value}
                  aria-label={`Automatically enable new ${label.toLowerCase()} seats`}
                  trackingLabel={`common-bidder-rules-auto-add-${section}-edit`}
                />
              </div>
              <p className="text-sm text-slate-400">
                When a new <strong>{AUTO_ADD_CAPTION_TYPE[section]}</strong> seat is added, it will be automatically enabled in this rule on every media type.
              </p>
            </div>
          )
        })}
      </div>
    </section>
  )
}
